import ErrorFallback from "@/ErrorFallBack";
import Loader from "@/components/common/Loader";
import useGetMe from "@/hooks/query/employees/useGetMe.ts";
import MainLayout from "@/layouts/main";
import { clearStorage, getStorage } from "@/utilize/storage";
import { Suspense, lazy, useEffect, useRef } from "react";
import { Navigate, createBrowserRouter, useLocation } from "react-router-dom";
import AclPage from "@/pages/acl";
import MspAdmins from "@/features/admins/adminsList";
import AddAdmin from "@/features/admins/add";
import MspAdminDetails from "@/features/admins/details";
import ChangePassword from "@/features/auth/resetpasswd/ChangePassword";
import OrganizationPage from "@/pages/organization";
import DefaultRedirect from "./DefaultRedirect";
import RequirePermission from "./RequirePermission";
const AuthLayout = lazy(() => import("@/layouts/auth"));

const Login = lazy(() => import("@/features/auth/login/Login"));
const Resetpasswd = lazy(
  () => import("@/features/auth/resetpasswd/Resetpasswd"),
);

const RegisterPage = lazy(
  () => import("@/features/auth/register/RegisterPage"),
);
const Forgot = lazy(() => import("@/features/auth/forgot/Forgot"));
const ForgotCheckMail = lazy(
  () => import("@/features/auth/forgot/ForgotCheckMail"),
);
const PageNotFound = lazy(() => import("@/pages/pageNotFound"));
const ItAdminsList = lazy(() => import("@/features/admins/itAdmin/adminsList"));
const AddItAdmin = lazy(() => import("@/features/admins/itAdmin/add"));
const DetailsItAdmin = lazy(() => import("@/features/admins/itAdmin/details"));
const BillingPage = lazy(() => import("@/pages/billing"));
const IntercomPage = lazy(
  () => import("@/pages/intercomTicket/IntercomTicketPage"),
);
const PasswordChanged = lazy(
  () => import("@/features/auth/resetpasswd/PasswordChanged"),
);

const Success = lazy(() => import("@/features/billing/components/Success"));
const Failed = lazy(() => import("@/features/billing/components/Failed"));

const Router = createBrowserRouter([
  {
    path: "",
    errorElement: <ErrorFallback />,
    children: [
      {
        path: "/auth/*",
        element: (
          <Suspense fallback={<Loader />}>
            <AuthLayout />
          </Suspense>
        ),
        children: [
          {
            path: "login",
            element: (
              <Suspense fallback={<Loader />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: "reset-password",
            element: (
              <Suspense fallback={<Loader />}>
                <Resetpasswd />
              </Suspense>
            ),
          },
          {
            path: "change-password",
            element: (
              <Suspense fallback={<Loader />}>
                <ChangePassword />
              </Suspense>
            ),
          },
          {
            path: "forgot",
            element: (
              <Suspense fallback={<Loader />}>
                <Forgot />
              </Suspense>
            ),
          },
          {
            path: "password-changed",
            element: (
              <Suspense fallback={<Loader />}>
                <PasswordChanged />
              </Suspense>
            ),
          },
          {
            path: "forgot/check-mail",
            element: (
              <Suspense fallback={<Loader />}>
                <ForgotCheckMail />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "billing/success-payment",
        element: (
          <Suspense fallback={<Loader />}>
            <Success />
          </Suspense>
        ),
      },
      {
        path: "billing/fail-payment",
        element: (
          <Suspense fallback={<Loader />}>
            <Failed />
          </Suspense>
        ),
      },
      // {
      //   path: "/auth/signup/*",
      //   element: (
      //     <Suspense fallback={<Loader />}>
      //       <SignUp />
      //     </Suspense>
      //   ),
      // },

      {
        path: "auth/register",
        element: (
          <Suspense fallback={<Loader />}>
            <RegisterPage />
          </Suspense>
        ),
      },

      {
        path: "/",
        element: (
          <RequireAuth>
            <MainLayout />
          </RequireAuth>
        ),
        children: [
          {
            index: true,
            element: <DefaultRedirect />,
          },
          {
            path: "/organizations/*",
            element: <RequirePermission routePrefix="/organizations" />,
            children: [
              {
                path: "*",
                element: (
                  <Suspense fallback={<Loader />}>
                    <OrganizationPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/billing/*",
            element: <RequirePermission routePrefix="/billing" />,
            children: [
              {
                path: "*",
                element: (
                  <Suspense fallback={<Loader />}>
                    <BillingPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/admins",
            element: <RequirePermission routePrefix="/admins" />,
            children: [
              {
                path: "",
                element: <MspAdmins />,
              },
              {
                path: "add",
                element: <AddAdmin />,
              },
              {
                path: ":id",
                element: <MspAdminDetails />,
              },
            ],
          },
          {
            path: "/it-admins",
            element: <RequirePermission routePrefix="/it-admins" />,
            children: [
              {
                path: "",
                element: (
                  <Suspense fallback={<Loader />}>
                    <ItAdminsList />
                  </Suspense>
                ),
              },
              {
                path: "add",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AddItAdmin />
                  </Suspense>
                ),
              },
              {
                path: ":id",
                element: (
                  <Suspense fallback={<Loader />}>
                    <DetailsItAdmin />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/acl/*",
            element: <RequirePermission routePrefix="/acl" />,
            children: [
              {
                path: "*",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AclPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/intercom/ticket/*",
            element: (
              <Suspense fallback={<Loader />}>
                <IntercomPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "/404",
        element: (
          <Suspense fallback={<Loader />}>
            <PageNotFound />
          </Suspense>
        ),
      },
    ],
  },
]);

function RequireAuth({ children }: { children: JSX.Element }) {
  const { accessToken, refreshToken } = getStorage();
  const getMe = useGetMe({ enabled: false });
  const location = useLocation();
  const hasRedirectedToLogin = useRef(false);

  useEffect(() => {
    if (accessToken || refreshToken) {
      getMe.refetch();
    }
  }, [accessToken, refreshToken]);

  if (!accessToken && !refreshToken) {
    if (!hasRedirectedToLogin.current) {
      localStorage.setItem(
        "redirectAfterLogin",
        location.pathname + location.search,
      );
      hasRedirectedToLogin.current = true;
    }
    return <Navigate replace to="/auth/login" />;
  }

  if (!getMe?.isFetched) return <Loader />;

  if (getMe.data) {
    return children;
  }

  if (!hasRedirectedToLogin.current) {
    clearStorage();
    localStorage.setItem(
      "redirectAfterLogin",
      location.pathname + location.search,
    );
    hasRedirectedToLogin.current = true;
    return <Navigate replace to="/auth/login" />;
  }
  return <Loader />;
}

export default Router;
